import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { selectors as clientSelectors } from 'store/clients';
import {
  types,
  KioskHardware,
  UpdateKioskHardwareRequest,
  CreateKioskHardwareRequest,
  DeleteKioskHardwareRequest,
  GetKioskHardwareRequest
} from './types';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';
import { omit } from 'lodash';

export function* getKioskHardwareAsync({ payload }: GetKioskHardwareRequest) {
  yield put({ type: types.GET_KIOSK_HARDWARE.PENDING, payload: true });

  try {
    const { kioskId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/hardware`);
    const response: KioskHardware[] = yield call(api.getAll);

    yield put({ type: types.GET_KIOSK_HARDWARE.SUCCESS, payload: response });
  } catch (err) {
    yield put({ type: types.GET_KIOSK_HARDWARE.ERROR, payload: 'Failed to get hardware' });
  } finally {
    yield put({ type: types.GET_KIOSK_HARDWARE.PENDING, payload: false });
  }
}

export function* createKioskHardwareAsync({ payload }: CreateKioskHardwareRequest) {
  yield put({ type: types.CREATE_KIOSK_HARDWARE.PENDING, payload: true });

  try {
    const { kioskId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/hardware`);
    yield call(api.create, { ...omit(payload, 'kioskId') });

    yield put({ type: types.CREATE_KIOSK_HARDWARE.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_KIOSK_HARDWARE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_KIOSK_HARDWARE.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.CREATE_KIOSK_HARDWARE.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.CREATE_KIOSK_HARDWARE.PENDING, payload: false });
  }
}

export function* updateKioskHardwareAsync({ payload }: UpdateKioskHardwareRequest) {
  yield put({ type: types.UPDATE_KIOSK_HARDWARE.PENDING, payload: true });

  try {
    const { kioskId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/hardware`);
    const response: KioskHardware = yield call(api.update, { ...omit(payload, 'kioskId') });

    yield put({ type: types.UPDATE_KIOSK_HARDWARE.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_KIOSK_HARDWARE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_KIOSK_HARDWARE.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_KIOSK_HARDWARE.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_KIOSK_HARDWARE.PENDING, payload: false });
  }
}

export function* deleteKioskHardwareAsync({ payload }: DeleteKioskHardwareRequest) {
  yield put({ type: types.DELETE_KIOSK_HARDWARE.PENDING, payload: true });

  try {
    const { id, kioskId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/hardware`);
    yield call(api.destroy, id);

    yield put({ type: types.DELETE_KIOSK_HARDWARE.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_KIOSK_HARDWARE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_KIOSK_HARDWARE.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.DELETE_KIOSK_HARDWARE.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.DELETE_KIOSK_HARDWARE.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_KIOSK_HARDWARE.REQUEST, getKioskHardwareAsync),
  takeLatest(types.UPDATE_KIOSK_HARDWARE.REQUEST, updateKioskHardwareAsync),
  takeLatest(types.CREATE_KIOSK_HARDWARE.REQUEST, createKioskHardwareAsync),
  takeLatest(types.DELETE_KIOSK_HARDWARE.REQUEST, deleteKioskHardwareAsync)
];
